import React from 'react';
import { motion } from 'motion/react';
import { Target, Sparkles, ShieldCheck, Heart } from 'lucide-react';
import SEO from '../../components/SEO';
import { Link } from 'react-router-dom';

const BrandVoice = () => {
  return (
    <div className="pt-24 pb-24 bg-[#030303] min-h-screen">
      <SEO 
        title="AI Brand Voice for Social Media - Stay Consistent"
        description="Train PlataPost's AI on your unique brand voice. Ensure every social media post reflects your tone, mission, and personality automatically."
      />

      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row items-center gap-20 mb-40">
          <div className="flex-1 space-y-8">
            <div className="inline-flex items-center gap-3 px-4 py-1.5 rounded-full glass-card border border-white/5 text-indigo-400 text-[10px] font-black uppercase tracking-widest leading-none">
              <Heart className="w-3.5 h-3.5" />
              <span>Identity First</span>
            </div>
            <h1 className="text-5xl lg:text-7xl font-black text-white leading-tight tracking-tighter">AI Brand <br /> <span className="text-indigo-500">Voice Training.</span></h1>
            <p className="text-xl text-gray-400 mb-8 leading-relaxed font-medium">
              Don't sound like a robot. Sound like YOU. Our AI analyzes your strategy documents and mission to master your unique brand voice.
            </p>
            <Link to="/dashboard" className="inline-flex px-10 py-5 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-indigo-700 transition-all shadow-2xl shadow-indigo-600/30">
              Train My Brand AI
            </Link>
          </div>
          <div className="flex-1 w-full glass-card rounded-[4rem] p-12 relative overflow-hidden border border-white/5 shadow-2xl shadow-indigo-600/5">
             <div className="absolute inset-0 bg-indigo-600/5 blur-[100px] pointer-events-none" />
             <div className="absolute top-0 left-0 p-8 opacity-10 blur-2xl">
               <Heart className="w-64 h-64 text-indigo-600 animate-pulse" />
             </div>
             <div className="relative space-y-10 group">
                <div className="flex items-center gap-6">
                  <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center font-black text-white shadow-xl shadow-white/5 border border-white/10">A</div>
                  <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: "100%" }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="h-full bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-6 group-hover:scale-105 transition-transform duration-500">
                  <div className="w-14 h-14 bg-indigo-600 rounded-2xl flex items-center justify-center font-black text-white shadow-xl shadow-indigo-600/20">B</div>
                  <div className="flex-1 h-3 bg-indigo-600/20 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: "100%" }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      className="h-full bg-indigo-500"
                    />
                  </div>
                </div>
                <div className="p-8 bg-white/5 rounded-3xl border border-white/10 border-l-4 border-l-indigo-500 shadow-xl shadow-black/20">
                  <p className="text-sm font-black text-indigo-400 uppercase tracking-widest mb-3">AI Analysis Result:</p>
                  <p className="text-lg font-medium text-gray-300">
                    "Matching tone detected: Professional, yet Casual, Informative, and Modern..."
                  </p>
                </div>
             </div>
          </div>
        </div>

        <section className="mb-40">
          <div className="text-center mb-24 space-y-4">
            <h2 className="text-4xl lg:text-5xl font-black text-white tracking-tighter leading-tight">Identity Without Limits</h2>
            <p className="text-gray-400 font-medium max-w-xl mx-auto">Ensuring every single word reflects your brand's unique signature.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Target, title: "Mission Alignment", desc: "The AI understands your core goals and weaves them into every post." },
              { icon: Sparkles, title: "Tone Selection", desc: "Choose from professional, witty, empathetic, or minimalist styles." },
              { icon: ShieldCheck, title: "Style Consistency", desc: "Automatically uses your brand's specific vocabulary and catchphrases." }
            ].map((feature, i) => (
              <div key={i} className="p-12 glass-card rounded-[3rem] border border-white/5 hover:bg-white/5 transition-all group">
                 <div className="w-16 h-16 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center mb-10 text-indigo-400 group-hover:scale-110 group-hover:rotate-6 transition-all shadow-xl shadow-indigo-600/5">
                   <feature.icon className="w-8 h-8" />
                 </div>
                 <h3 className="text-3xl font-black text-white mb-6 tracking-tight">{feature.title}</h3>
                 <p className="text-gray-400 leading-relaxed font-medium">{feature.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="text-center glass-card rounded-[4rem] p-24 border border-white/5 relative overflow-hidden shadow-2xl shadow-indigo-600/5">
           <div className="absolute inset-0 bg-indigo-600/5 blur-[120px] pointer-events-none" />
           <h2 className="text-4xl lg:text-5xl font-black text-white mb-10 tracking-tighter leading-tight relative z-10">Stop sounding generic.</h2>
           <Link to="/booking" className="relative z-10 inline-flex px-12 py-6 bg-indigo-600 text-white rounded-2xl font-black text-sm uppercase tracking-[0.2em] hover:bg-indigo-700 transition-all shadow-2xl shadow-indigo-600/30">
             Get Demo
           </Link>
        </div>
      </div>
    </div>
  );
};

export default BrandVoice;
